import { useEffect, useMemo, useState } from 'react';
import { listInvestorPrograms } from '../../api/investorApi';
import { DataTable } from '../../components/ui/DataTable';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { humanize } from './investorLabels';

export function InvestorProgramsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listInvestorPrograms({ page, pageSize });
        if (cancelled) return;
        setRows(response?.items || []);
        setTotal(response?.meta?.total ?? 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load programs.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, reloadToken]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Program',
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">{row.name}</span>
            <span className="block font-mono text-[11px] text-eorig-muted">{row.code}</span>
          </div>
        ),
      },
      { key: 'originator', header: 'Originator' },
      { key: 'custodian', header: 'Custodian', render: (row) => row.custodian || '—' },
      { key: 'packages', header: 'Packages', align: 'right' },
      {
        key: 'admission_profile_status',
        header: 'Admission rules',
        render: (row) =>
          row.admission_profile_status ? (
            <StatusPill
              value={row.admission_profile_status}
              label={humanize(row.admission_profile_status)}
            />
          ) : (
            <span className="text-eorig-muted">None</span>
          ),
      },
      {
        key: 'is_active',
        header: 'Active',
        render: (row) => (
          <StatusPill value={row.is_active ? 'ACTIVE' : 'INACTIVE'} label={row.is_active ? 'Active' : 'Inactive'} />
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((v) => v + 1)} /> : null}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage="No programs."
        minWidth={760}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
